import { useTranslation } from "react-i18next";
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";

type Permission = {
    groupName: string;
    id: string;
    key: string;
    name: string;
};

type Role = {
    id: string;
    name: string;
    permissions: Permission[];
};

interface RolesTableProps {
    roles: Role[];
    loading: boolean;
    onItemClick?: (id: string) => void;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const RolesTable = ({
    roles,
    loading,
    onItemClick,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
}: RolesTableProps) => {
    const { t } = useTranslation("common", {
        keyPrefix: "roles-overview",
    });

    const { t: tPermissionKey, i18n } = useTranslation("common", {
        keyPrefix: "permission-keys",
    });

    if (roles.length === 0 && !loading) {
        return (
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {t("table.no-data")}
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="providers table">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("table.name")}</TableCell>
                        <TableCell>{t("table.permissions")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell align="center">
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 2,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        roles.map((role) => (
                            <TableRow
                                key={role.id}
                                onClick={() => onItemClick?.(role.id)}
                                sx={{
                                    "&:last-child td, &:last-child th": {
                                        border: 0,
                                    },
                                    ...(onItemClick && {
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                        },
                                    }),
                                }}
                            >
                                <TableCell component="th" scope="row">
                                    {role.name}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        maxWidth: 200,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    onClick={() => onItemClick?.(role.id)}
                                >
                                    <Tooltip
                                        title={
                                            role.permissions
                                                .map((p) =>
                                                    i18n.exists(
                                                        `permission-keys.${p.key}`
                                                    )
                                                        ? tPermissionKey(p.key)
                                                        : p.name
                                                )
                                                .join(", ") || "-"
                                        }
                                    >
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                display: "block",
                                            }}
                                        >
                                            {role.permissions
                                                .map((p) =>
                                                    i18n.exists(
                                                        `permission-keys.${p.key}`
                                                    )
                                                        ? tPermissionKey(p.key)
                                                        : p.name
                                                )
                                                .join(", ") || "-"}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
            <TablePagination
                rowsPerPageOptions={[5, 15, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
        </TableContainer>
    );
};

export default RolesTable;
